"use client"

import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Edit2, DollarSign, Calendar, Users, BarChart4, Tag, LineChart, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import FormModal from '@/components/FormModal';

interface DealDetailsProps {
  publicId: string;
}

export default function Deal({ publicId }: DealDetailsProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [deal, setDeal] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchDeal = async () => {
      setIsLoading(true);
      try {
        // Replace with actual API call when implemented
        // const response = await DealService.getDeal(publicId);
        // Mocking data for now
        const mockDeal = {
          name: "Enterprise SaaS Agreement",
          description: "Annual subscription for software services",
          value: 75000,
          currency: "USD",
          stage: "Negotiation",
          probability: 70,
          expectedCloseDate: "2023-12-15",
          actualCloseDate: null,
          customer: {
            name: "Acme Corporation",
            id: "cust-123"
          },
          owner: {
            name: "Jane Smith",
            id: "user-456"
          },
          pipeline: "Sales Pipeline",
          createdAt: "2023-09-01",
          updatedAt: "2023-11-10",
          activities: [
            {
              type: "Email",
              date: "2023-11-10",
              summary: "Follow-up on contract terms"
            },
            {
              type: "Meeting",
              date: "2023-10-28",
              summary: "Negotiation meeting with legal team"
            }
          ]
        };
        
        setTimeout(() => {
          setDeal(mockDeal);
          setIsLoading(false);
        }, 500);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load deal details",
          variant: "destructive",
        });
        setIsLoading(false);
      }
    };
    
    if (publicId) {
      fetchDeal();
    }
  }, [publicId, toast]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading deal details...</p>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <p className="text-muted-foreground">Deal not found</p>
        <Button onClick={() => window.history.back()} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">
              {deal.name}
            </h1>
            <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
              {deal.stage}
            </Badge>
          </div>
          
          <Button 
            onClick={() => setModalOpen(true)} 
            variant="outline"
            className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
          >
            <Edit2 className="w-4 h-4" />
            Edit Deal
          </Button>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Deal Overview */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Deal Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col items-center py-4">
                <div className="text-3xl font-bold text-primary">
                  {deal.currency} {deal.value.toLocaleString()}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{deal.pipeline}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium">Probability</p>
                  <Badge variant="outline">{deal.probability}%</Badge>
                </div>
                <Progress value={deal.probability} className="h-2" />
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Expected Close Date</p>
                    <p className="text-sm text-muted-foreground">{deal.expectedCloseDate}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Customer</p>
                    <p className="text-sm text-muted-foreground">{deal.customer.name}</p>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Quick Actions */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Quick Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <BarChart4 className="w-4 h-4 mr-2" />
                    Activities
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <LineChart className="w-4 h-4 mr-2" />
                    Pipeline
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Deal Details */}
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid grid-cols-2 w-full md:w-[400px]">
                <TabsTrigger value="details">Deal Details</TabsTrigger>
                <TabsTrigger value="activities">Activities</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Description</p>
                        <p className="font-medium">{deal.description}</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Owner</p>
                          <p className="font-medium">{deal.owner.name}</p>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Created</p>
                          <p className="font-medium">{deal.createdAt}</p>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Last Updated</p>
                          <p className="font-medium">{deal.updatedAt}</p>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Stage</p>
                          <p className="font-medium">{deal.stage}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="activities" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {deal.activities.length > 0 ? (
                        <div className="space-y-4">
                          {deal.activities.map((activity: any, index: number) => (
                            <div key={index} className="flex items-start gap-4 p-4 border rounded-lg">
                              <div className="bg-primary/10 p-2 rounded-full">
                                {activity.type === "Email" && <Mail className="w-4 h-4 text-primary" />}
                                {activity.type === "Meeting" && <Users className="w-4 h-4 text-primary" />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{activity.type}</p>
                                  <p className="text-xs text-muted-foreground">{activity.date}</p>
                                </div>
                                <p className="text-sm mt-1">{activity.summary}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground italic">No activities found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      
      {/* Edit Modal - To be implemented */}
      {isModalOpen && (
        <FormModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          title="Edit Deal"
        >
          <div className="p-4">
            <p>Deal edit form will be implemented here.</p>
          </div>
        </FormModal>
      )}
    </div>
  );
} 